import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { weekLabel, volledigeDatum } from '../utils/datum';

const PRIO_CL = { spoed: '#A32D2D', hoog: '#854F0B', normaal: '#3B6D11' };
const PRIO_BG = { spoed: '#FDEDEC', hoog: '#FDF3E3', normaal: '#EAF3DE' };

function parseerBestelling(ref) {
  if (!ref) return {};
  const parts = ref.trim().split(/\s+/);
  return { leverancier: parts[0], blNr: parts.slice(1).join(' ') };
}

function dagLabel(d) {
  if (d == null) return '—';
  if (d < 0) return <span style={{ color: '#A32D2D', fontWeight: 700 }}>{Math.abs(d)}d te laat</span>;
  if (d === 0) return <span style={{ color: '#A32D2D', fontWeight: 700 }}>Vandaag!</span>;
  if (d <= 3)  return <span style={{ color: '#854F0B', fontWeight: 600 }}>{d} dagen</span>;
  return <span style={{ color: '#555' }}>{d} dagen</span>;
}

function DatumCell({ origineel, herzien, onEdit }) {
  const vandaag = new Date(); vandaag.setHours(0, 0, 0, 0);
  const actief = herzien || origineel;
  const isLaat = actief && new Date(actief) < vandaag;
  const isHerzien = !!herzien;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div>
        {actief ? (
          <>
            <span style={{ color: isLaat ? '#A32D2D' : '#333', fontWeight: isLaat ? 600 : 400 }}
                  title={volledigeDatum(actief)}>
              {weekLabel(actief)}
              {isLaat && <span style={{ fontSize: 10, marginLeft: 4 }}>⚠ over tijd</span>}
            </span>
            {isHerzien && (
              <>
                <span style={{ fontSize: 10, marginLeft: 6, padding: '1px 6px', borderRadius: 99, background: '#FFF2CC', color: '#7F6000', border: '1px solid #F0C060', fontWeight: 600 }}>herzien</span>
                <div style={{ fontSize: 10, color: '#AAA', marginTop: 1 }} title={volledigeDatum(origineel)}>orig: {weekLabel(origineel)}</div>
              </>
            )}
          </>
        ) : (
          <span style={{ color: '#CCC', fontSize: 12 }}>—</span>
        )}
      </div>
      <button onClick={onEdit} title="Datum aanpassen"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '2px 4px', fontSize: 13, borderRadius: 4, flexShrink: 0 }}>
        ✏️
      </button>
    </div>
  );
}

export default function BesteldPage() {
  const [hoofdOrders, setHoofdOrders] = useState([]);
  const [extraMat, setExtraMat]       = useState([]);
  const [laden, setLaden]             = useState(true);
  const [bezig, setBezig]             = useState(null);
  const [zoek, setZoek]               = useState('');
  const [tab, setTab]                 = useState('alles');
  const [prioriteitFilter, setPrioriteitFilter] = useState('');
  const [toonAlleenOverdue, setToonAlleenOverdue] = useState(false);
  const [datumEdit, setDatumEdit]     = useState(null); // { type, id, orderId, huidigeDatum }
  const [undoToast, setUndoToast]     = useState(null); // { type, orderId, matId, label, origItem, timer }
  const nav = useNavigate();

  const laad = async () => {
    const [ordersRes, matRes] = await Promise.all([
      api.get('/orders?materiaal_status=besteld'),
      api.get('/orders/alle-materialen?status=besteld'),
    ]);
    setHoofdOrders(ordersRes.data);
    setExtraMat(matRes.data);
    setLaden(false);
  };

  useEffect(() => { laad(); }, []);

  const toonUndoToast = (data) => {
    if (undoToast?.timer) clearTimeout(undoToast.timer);
    const timer = setTimeout(() => setUndoToast(null), 6000);
    setUndoToast({ ...data, timer });
  };

  const markeerHoofdGeleverd = async (orderId) => {
    setBezig(`hoofd-${orderId}`);
    const origItem = hoofdOrders.find(x => x.id === orderId);
    try {
      await api.patch(`/orders/${orderId}/materiaalstatus`, { materiaal_status: 'geleverd' });
      setHoofdOrders(o => o.filter(x => x.id !== orderId));
      toonUndoToast({ type: 'hoofd', orderId, label: `${origItem?.ordernummer} — ${origItem?.klant}`, origItem });
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setBezig(null);
  };

  const markeerExtraGeleverd = async (orderId, matId) => {
    setBezig(`mat-${matId}`);
    const origItem = extraMat.find(x => x.id === matId);
    try {
      await api.patch(`/orders/${orderId}/materialen/${matId}/status`, { status: 'geleverd' });
      setExtraMat(m => m.filter(x => x.id !== matId));
      toonUndoToast({ type: 'extra', orderId, matId, label: `${origItem?.ordernummer} — ${origItem?.naam}`, origItem });
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setBezig(null);
  };

  const undoGeleverd = async () => {
    if (!undoToast) return;
    if (undoToast.timer) clearTimeout(undoToast.timer);
    const toast = undoToast;
    setUndoToast(null);
    try {
      if (toast.type === 'hoofd') {
        await api.patch(`/orders/${toast.orderId}/materiaalstatus`, { materiaal_status: 'besteld' });
        if (toast.origItem) setHoofdOrders(o => [toast.origItem, ...o]);
      } else {
        await api.patch(`/orders/${toast.orderId}/materialen/${toast.matId}/status`, { status: 'besteld' });
        if (toast.origItem) setExtraMat(m => [toast.origItem, ...m]);
      }
    } catch (e) { alert('Fout bij ongedaan maken: ' + (e.response?.data?.error || e.message)); }
  };

  const slaaDatumOp = async () => {
    if (!datumEdit?.datum) return;
    const { type, id, orderId, datum } = datumEdit;
    setDatumEdit(null);
    try {
      if (type === 'hoofd') {
        await api.patch(`/orders/${orderId}/materiaalleverdatum`, { leverdatum: datum });
        setHoofdOrders(list => list.map(o => {
          if (o.id !== orderId) return o;
          if (!o.materiaal_leverdatum_verwacht) return { ...o, materiaal_leverdatum_verwacht: datum };
          return { ...o, materiaal_leverdatum_verwacht_herzien: datum };
        }));
      } else {
        await api.patch(`/orders/${orderId}/materialen/${id}/leverdatum`, { leverdatum: datum });
        setExtraMat(list => list.map(m => {
          if (m.id !== id) return m;
          if (!m.leverdatum_verwacht) return { ...m, leverdatum_verwacht: datum };
          return { ...m, leverdatum_verwacht_herzien: datum };
        }));
      }
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
  };

  const actiefHoofdDatum = o => o.materiaal_leverdatum_verwacht_herzien || o.materiaal_leverdatum_verwacht;
  const actiefExtraDatum = m => m.leverdatum_verwacht_herzien || m.leverdatum_verwacht;

  const gefilterdHoofd = useMemo(() => hoofdOrders.filter(o => {
    if (prioriteitFilter && o.prioriteit !== prioriteitFilter) return false;
    if (toonAlleenOverdue) {
      const d = actiefHoofdDatum(o); if (!d || new Date(d) >= new Date()) return false;
    }
    if (!zoek) return true;
    const q = zoek.toLowerCase();
    return `${o.ordernummer} ${o.klant} ${o.artikel_omschrijving} ${o.materiaal_beschrijving || ''} ${o.leverancier_naam || ''}`.toLowerCase().includes(q);
  }), [hoofdOrders, zoek, prioriteitFilter, toonAlleenOverdue]);

  const gefilterdExtra = useMemo(() => extraMat.filter(m => {
    if (prioriteitFilter && m.prioriteit !== prioriteitFilter) return false;
    if (toonAlleenOverdue) {
      const d = actiefExtraDatum(m); if (!d || new Date(d) >= new Date()) return false;
    }
    if (!zoek) return true;
    const q = zoek.toLowerCase();
    return `${m.ordernummer} ${m.klant} ${m.naam} ${m.referentie || ''} ${m.leverancier_naam || ''}`.toLowerCase().includes(q);
  }), [extraMat, zoek, prioriteitFilter, toonAlleenOverdue]);

  const overdueHoofd = hoofdOrders.filter(o => {
    const d = actiefHoofdDatum(o); return d && new Date(d) < new Date();
  }).length;
  const overdueExtra = extraMat.filter(m => {
    const d = actiefExtraDatum(m); return d && new Date(d) < new Date();
  }).length;
  const totaalOverdue = overdueHoofd + overdueExtra;
  const totaal = hoofdOrders.length + extraMat.length;

  const tabel = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
  const th = { padding: '9px 14px', fontWeight: 500, fontSize: 11, color: '#888', textAlign: 'left', background: '#F7F9FC', borderBottom: '1px solid #E8E8E8' };
  const td = { padding: '10px 14px', borderBottom: '1px solid #F0F0F0', verticalAlign: 'middle' };

  const toonHoofd = tab === 'alles' || tab === 'hoofd';
  const toonExtra = tab === 'alles' || tab === 'extra';

  const DatumModal = () => {
    if (!datumEdit) return null;
    const heeftDatum = datumEdit.type === 'hoofd'
      ? !!hoofdOrders.find(o => o.id === datumEdit.orderId)?.materiaal_leverdatum_verwacht
      : !!extraMat.find(m => m.id === datumEdit.id)?.leverdatum_verwacht;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 320, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#1F3864', marginBottom: 4 }}>
            {heeftDatum ? 'Leverdatum herzien' : 'Leverdatum ingeven'}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
            {heeftDatum
              ? 'De bestaande datum wordt bewaard als origineel. De nieuwe datum krijgt een "herzien" markering.'
              : 'Geef de verwachte leverdatum van het materiaal in.'}
          </div>
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>
            {heeftDatum ? 'Nieuwe verwachte leverdatum' : 'Verwachte leverdatum materiaal'}
          </label>
          <input type="date" value={datumEdit.datum || ''}
            onChange={e => setDatumEdit(d => ({ ...d, datum: e.target.value }))}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 16 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setDatumEdit(null)}
              style={{ padding: '7px 16px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#fff', color: '#555' }}>
              Annuleren
            </button>
            <button onClick={slaaDatumOp} disabled={!datumEdit.datum}
              style={{ padding: '7px 16px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: datumEdit.datum ? 'pointer' : 'not-allowed', opacity: datumEdit.datum ? 1 : 0.5 }}>
              Opslaan
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <DatumModal />

      {/* Undo toast */}
      {undoToast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
          background: '#1F3864', color: '#fff', borderRadius: 10, padding: '12px 18px',
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)', fontSize: 13,
        }}>
          <span>✓ Gemarkeerd als geleverd</span>
          <button onClick={undoGeleverd} style={{
            background: '#fff', color: '#1F3864', border: 'none', borderRadius: 6,
            padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>Ongedaan maken</button>
          <button onClick={() => { clearTimeout(undoToast.timer); setUndoToast(null); }}
            style={{ background: 'none', border: 'none', color: '#AAA', cursor: 'pointer', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1F3864' }}>Besteld materiaal</h1>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
            Materiaal in afwachting van levering —{' '}
            {laden ? '...' : <><strong>{totaal} item{totaal !== 1 ? 's' : ''}</strong>{totaalOverdue > 0 && <span style={{ color: '#A32D2D', marginLeft: 8 }}>⚠ {totaalOverdue} over verwachte leverdatum</span>}</>}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={zoek} onChange={e => setZoek(e.target.value)}
          placeholder="Zoeken op order, klant, materiaal, leverancier..."
          style={{ flex: 1, minWidth: 220, padding: '7px 12px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { key: 'alles', label: `Alles (${hoofdOrders.length + extraMat.length})` },
            { key: 'hoofd', label: `Hoofdmateriaal (${hoofdOrders.length})` },
            { key: 'extra', label: `Extra materialen (${extraMat.length})` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '6px 14px', border: '1px solid #DDD', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              background: tab === t.key ? '#185FA5' : '#fff',
              color: tab === t.key ? '#fff' : '#555',
              fontWeight: tab === t.key ? 600 : 400,
            }}>{t.label}</button>
          ))}
        </div>
      </div>
      {/* Prioriteit + overdue chips */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: '', label: 'Alle prioriteiten' },
          { key: 'spoed',   label: '🔴 Spoed',   kleur: '#A32D2D' },
          { key: 'hoog',    label: '🟠 Hoog',    kleur: '#854F0B' },
          { key: 'normaal', label: '🟢 Normaal', kleur: '#3B6D11' },
        ].map(p => (
          <button key={p.key} onClick={() => setPrioriteitFilter(p.key)} style={{
            padding: '4px 12px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
            border: prioriteitFilter === p.key ? `1.5px solid ${p.kleur || '#555'}` : '1px solid #DDD',
            background: prioriteitFilter === p.key ? (p.kleur ? p.kleur + '18' : '#F0F0F0') : '#fff',
            color: prioriteitFilter === p.key ? (p.kleur || '#333') : '#555',
            fontWeight: prioriteitFilter === p.key ? 600 : 400,
          }}>{p.label}</button>
        ))}
        {totaalOverdue > 0 && (
          <button onClick={() => setToonAlleenOverdue(t => !t)} style={{
            padding: '4px 12px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
            border: toonAlleenOverdue ? '1.5px solid #A32D2D' : '1px solid #DDD',
            background: toonAlleenOverdue ? '#FDEDEC' : '#fff',
            color: toonAlleenOverdue ? '#A32D2D' : '#555',
            fontWeight: toonAlleenOverdue ? 600 : 400,
          }}>⚠ Alleen overdue ({totaalOverdue})</button>
        )}
      </div>

      {laden ? (
        <div style={{ padding: 40, color: '#888', textAlign: 'center' }}>Laden...</div>
      ) : totaal === 0 ? (
        <div style={{ background: '#EAF3DE', border: '1px solid #C0DD97', borderRadius: 8, padding: '24px', color: '#27500A', fontSize: 14 }}>
          Geen openstaande leveringen.
        </div>
      ) : (
        <>
          {/* Hoofdmateriaal */}
          {toonHoofd && gefilterdHoofd.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #E0E0E0', borderRadius: 10, marginBottom: 20, overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px', background: '#E6F1FB', borderBottom: '1px solid #BDD4EE', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: 99, background: '#185FA5' }} />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#0C3A70' }}>Hoofdmateriaal orders</span>
                <span style={{ fontSize: 12, color: '#185FA5' }}>{gefilterdHoofd.length} order{gefilterdHoofd.length !== 1 ? 's' : ''}</span>
              </div>
              <table style={tabel}>
                <thead>
                  <tr>
                    {['Order', 'Klant / Artikel', 'Materiaal', 'Leverancier', 'Verwachte levering', 'Leverdatum order', 'Prioriteit', ''].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gefilterdHoofd.map(o => {
                    const actiefDatum = actiefHoofdDatum(o);
                    const isOverdue = actiefDatum && new Date(actiefDatum) < new Date();
                    return (
                      <tr key={o.id} style={{ background: isOverdue ? '#FFF5F5' : o.dagen_resterend < 0 ? '#FFF8F8' : '#fff' }}>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {o.familie_kleur && <div style={{ width: 4, height: 18, borderRadius: 2, background: o.familie_kleur }} />}
                            <span onClick={() => nav(`/orders/${o.id}`)} style={{ color: '#185FA5', fontWeight: 700, cursor: 'pointer' }}>{o.ordernummer}</span>
                          </div>
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 500, color: '#333' }}>{o.klant}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{o.artikel_omschrijving}</div>
                        </td>
                        <td style={td}>
                          <div style={{ color: '#333' }}>{o.materiaal_beschrijving || <span style={{ color: '#CCC' }}>—</span>}</div>
                          {parseerBestelling(o.materiaal_referentie).blNr && (
                            <div style={{ fontSize: 11, color: '#888' }}>BL: {parseerBestelling(o.materiaal_referentie).blNr}</div>
                          )}
                        </td>
                        <td style={{ ...td, color: '#555' }}>
                          {o.leverancier_naam || parseerBestelling(o.materiaal_referentie).leverancier || <span style={{ color: '#CCC' }}>—</span>}
                        </td>
                        <td style={td}>
                          <DatumCell
                            origineel={o.materiaal_leverdatum_verwacht}
                            herzien={o.materiaal_leverdatum_verwacht_herzien}
                            onEdit={() => setDatumEdit({ type: 'hoofd', id: o.id, orderId: o.id, datum: '' })}
                          />
                        </td>
                        <td style={td}>
                          <div style={{ fontSize: 12 }} title={volledigeDatum(o.leverdatum)}>{weekLabel(o.leverdatum)}</div>
                          <div style={{ fontSize: 11, marginTop: 2 }}>{dagLabel(o.dagen_resterend)}</div>
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: PRIO_BG[o.prioriteit], color: PRIO_CL[o.prioriteit] }}>
                            {o.prioriteit}
                          </span>
                        </td>
                        <td style={td}>
                          <button onClick={() => markeerHoofdGeleverd(o.id)} disabled={bezig === `hoofd-${o.id}`}
                            style={{ padding: '5px 14px', background: '#1A7A3C', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {bezig === `hoofd-${o.id}` ? 'Bezig...' : 'Geleverd'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Extra materialen */}
          {toonExtra && gefilterdExtra.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #E0E0E0', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px', background: '#E6F1FB', borderBottom: '1px solid #BDD4EE', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: 99, background: '#185FA5' }} />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#0C3A70' }}>Extra materialen</span>
                <span style={{ fontSize: 12, color: '#185FA5' }}>{gefilterdExtra.length} item{gefilterdExtra.length !== 1 ? 's' : ''}</span>
              </div>
              <table style={tabel}>
                <thead>
                  <tr>
                    {['Order', 'Klant / Artikel', 'Materiaal', 'Leverancier', 'Verwachte levering', 'Leverdatum order', 'Prioriteit', ''].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gefilterdExtra.map(m => {
                    const actiefDatum = actiefExtraDatum(m);
                    const isOverdue = actiefDatum && new Date(actiefDatum) < new Date();
                    return (
                      <tr key={m.id} style={{ background: isOverdue ? '#FFF5F5' : m.dagen_resterend < 0 ? '#FFF8F8' : '#fff' }}>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {m.familie_kleur && <div style={{ width: 4, height: 18, borderRadius: 2, background: m.familie_kleur }} />}
                            <span onClick={() => nav(`/orders/${m.order_id}`)} style={{ color: '#185FA5', fontWeight: 700, cursor: 'pointer' }}>{m.ordernummer}</span>
                          </div>
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 500, color: '#333' }}>{m.klant}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{m.artikel_omschrijving}</div>
                        </td>
                        <td style={td}>
                          <div style={{ color: '#333' }}>{m.naam}</div>
                          {m.referentie && <div style={{ fontSize: 11, color: '#888' }}>{m.referentie}</div>}
                          {m.tanden_per_stuk ? (
                            <span style={{ fontSize: 11, color: '#8A5C00', background: '#FFFBF0', border: '1px solid #F0C060', borderRadius: 4, padding: '1px 6px', marginTop: 2, display: 'inline-block' }}>
                              {m.totaal_tanden} tanden
                            </span>
                          ) : null}
                        </td>
                        <td style={{ ...td, color: '#555' }}>{m.leverancier_naam || <span style={{ color: '#CCC' }}>—</span>}</td>
                        <td style={td}>
                          <DatumCell
                            origineel={m.leverdatum_verwacht}
                            herzien={m.leverdatum_verwacht_herzien}
                            onEdit={() => setDatumEdit({ type: 'extra', id: m.id, orderId: m.order_id, datum: '' })}
                          />
                        </td>
                        <td style={td}>
                          <div style={{ fontSize: 12 }} title={volledigeDatum(m.leverdatum)}>{weekLabel(m.leverdatum)}</div>
                          <div style={{ fontSize: 11, marginTop: 2 }}>{dagLabel(m.dagen_resterend)}</div>
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: PRIO_BG[m.prioriteit], color: PRIO_CL[m.prioriteit] }}>
                            {m.prioriteit}
                          </span>
                        </td>
                        <td style={td}>
                          <button onClick={() => markeerExtraGeleverd(m.order_id, m.id)} disabled={bezig === `mat-${m.id}`}
                            style={{ padding: '5px 14px', background: '#1A7A3C', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {bezig === `mat-${m.id}` ? 'Bezig...' : 'Geleverd'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {(gefilterdHoofd.length === 0 && gefilterdExtra.length === 0) && (
            <div style={{ padding: 24, color: '#AAA', textAlign: 'center', fontStyle: 'italic' }}>Geen resultaten voor "{zoek}"</div>
          )}
        </>
      )}
    </div>
  );
}
